import {
  FormFileInput,
  FormInput,
  FormSectionHeader,
  FormSelect,
  FormTextarea,
  Heading,
  Loader,
} from "@/components";
import { Button, Label, RadioGroup, RadioGroupItem } from "@/components/ui";
import { FormProvider } from "react-hook-form";
import useAddEdit from "./useAddEdit";
import {
  ASSIGN_TO,
  ASSIGNMENT_DATE,
  ATTACHMENTS,
  COMMENTS,
  COMMON_AREA,
  DESCRIPTION,
  INCIDENT_CATEGORY,
  INCIDENT_TYPE,
  PRIORITY,
  priorityOptions,
  PROPERTY_SPECIFIC,
  REPORTEE,
  STATUS,
} from "./consts";
import { cn, incidentStatus } from "@/lib/utils";
import { Trash2 } from "lucide-react";
import moment from "moment";

const AddEdit = () => {
  const {
    isAdd,
    breadcrumbData,
    onBackClick,
    form,
    onSubmit,
    requiredFields,
    isAddPending,
    onCancel,
    attachmentRef,
    onOptionChanges,
    incidentCategory,
    isIncidentCategoryLoading,
    incidentType,
    isIncidentTypeLoading,
    isLoadingProperties,
    onDeleteAttachment,
    isIncidentDataLoading,
    isView,
    keys,
    isUpdatePending,
    onViewImage,
    incidentComments,
    isIncidentCommentsLoading,
    loadMoreOptions,
    userOptions,
    isUserLoading,
    hasMore
  } = useAddEdit();
  const { errors } = form.formState;

  return (
    <div className="p-4 md:p-6">
      <Heading
        title={breadcrumbData[breadcrumbData.length - 1].name}
        showBack
        breadcrumbData={breadcrumbData}
        onBackClick={onBackClick}
      />

      {isIncidentCategoryLoading ||
      isIncidentTypeLoading ||
      isLoadingProperties ||
      isIncidentDataLoading ||
      isIncidentCommentsLoading ? (
        <Loader />
      ) : (
        <>
          <FormProvider {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <div className="bg-white mt-[20px] rounded-lg px-[30px] py-[25px]">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[30px]">
                  <FormSelect
                    key={keys.incidentType}
                    name={INCIDENT_TYPE}
                    control={form.control}
                    label="Incident Type"
                    placeholder="Please select"
                    error={errors[INCIDENT_TYPE]?.message}
                    isRequired={requiredFields.includes(INCIDENT_TYPE)}
                    options={(incidentType?.data || []).map((val: any) => ({
                      label: val.incidentType?.toString(),
                      value: val.incidentTypeId?.toString(),
                    }))}
                    disabled={isView}
                  />
                  <FormSelect
                    key={keys.incidentCategory}
                    name={INCIDENT_CATEGORY}
                    control={form.control}
                    label="Incident Category"
                    placeholder="Please select"
                    error={errors[INCIDENT_CATEGORY]?.message}
                    isRequired={requiredFields.includes(INCIDENT_CATEGORY)}
                    options={(incidentCategory?.data || []).map((val: any) => ({
                      label: val.categoryName?.toString(),
                      value: val.categoryId?.toString(),
                    }))}
                    disabled={isView}
                  />
                  <RadioGroup
                    onValueChange={onOptionChanges}
                    value={form.watch(PROPERTY_SPECIFIC) ? "1" : "2"}
                    className="flex items-start gap-3 text-gray-400"
                  >
                    <div
                      className={cn(
                        "flex items-center space-x-2",
                        form.watch(PROPERTY_SPECIFIC)
                          ? "text-primary"
                          : "text-gray-400"
                      )}
                    >
                      <RadioGroupItem
                        value="1"
                        id="option-one"
                        className={
                          form.watch(PROPERTY_SPECIFIC) ? "border-primary" : ""
                        }
                        disabled={isView}
                      />
                      <Label htmlFor="option-one">Property Specific</Label>
                    </div>
                    <div
                      className={cn(
                        "flex items-center space-x-2",
                        form.watch(COMMON_AREA) ? "text-primary" : ""
                      )}
                    >
                      <RadioGroupItem
                        value="2"
                        id="option-two"
                        className={
                          form.watch(COMMON_AREA) ? "border-primary" : ""
                        }
                        disabled={isView}
                      />
                      <Label htmlFor="option-two">Common Area</Label>
                    </div>
                  </RadioGroup>

                  {/* <FormSelect
                name={ASSIGN_TO}
                control={form.control}
                label="Assign To"
                placeholder="Please select"
                error={errors[ASSIGN_TO]?.message}
                isRequired={requiredFields.includes(ASSIGN_TO)}
                options={[]}
              /> */}
                  {form.watch(PROPERTY_SPECIFIC) ? (
                    <>
                      <FormSelect
                        key={keys.responsible}
                        name={ASSIGN_TO}
                        control={form.control}
                        label="Responsible"
                        placeholder="Please select"
                        error={errors[ASSIGN_TO]?.message}
                        isRequired={requiredFields.includes(ASSIGN_TO)}
                        options={userOptions}
                        isLoadingMore={isUserLoading}
                        hasMore={hasMore}
                        onLoadMore={loadMoreOptions}
                        disabled={isView}
                      />
                      <FormInput
                        name={ASSIGNMENT_DATE}
                        control={form.control}
                        label="Assignment Date"
                        placeholder="Please enter"
                        error={errors[ASSIGNMENT_DATE]?.message}
                        isRequired={requiredFields.includes(ASSIGNMENT_DATE)}
                        disabled
                      />
                    </>
                  ) : (
                    <></>
                  )}
                  <FormInput
                    name={REPORTEE}
                    control={form.control}
                    label="Reportee"
                    placeholder="Please select"
                    error={errors[REPORTEE]?.message}
                    isRequired={requiredFields.includes(REPORTEE)}
                    disabled
                  />
                  <FormSelect
                    key={keys.priority}
                    name={PRIORITY}
                    control={form.control}
                    label="Priority"
                    placeholder="Please select"
                    error={errors[PRIORITY]?.message}
                    isRequired={requiredFields.includes(PRIORITY)}
                    options={priorityOptions}
                    disabled={isView}
                  />
                  <FormSelect
                    key={keys.status}
                    name={STATUS}
                    control={form.control}
                    label="Status"
                    placeholder="Please select"
                    error={errors[STATUS]?.message}
                    isRequired={requiredFields.includes(STATUS)}
                    options={Object.entries(incidentStatus)?.map(
                      ([_, val]: any) => {
                        return {
                          label: val.label,
                          value: val.value,
                          disabled: isAdd && val.value != incidentStatus.open.value,
                        };
                      }
                    )}
                    disabled={isView}
                  />
                  <FormFileInput
                    fileInputRef={attachmentRef}
                    name={ATTACHMENTS}
                    control={form.control}
                    label="Upload File"
                    error={errors[ATTACHMENTS]?.message}
                    multiple
                    disabled={isView}
                    accept="image/*,.pdf,video/*,.zip"
                    maxSize={5}
                    keepPreviousFiles
                    info="Accepted File(s): PNG, JPG, PDF, Video, ZIP | Max File Size: 5 MB"
                  />
                  <div className="row-span-2 flex flex-col gap-1 text-[12px]">
                    {form.watch(ATTACHMENTS) ? (
                      form
                        .watch(ATTACHMENTS)
                        ?.map((file: any, index: number) => (
                          <div className="flex flex-row gap-3 items-center">
                            <div
                              onClick={() =>
                                onViewImage(
                                  file.attachmentId
                                    ? file.presignedUrl
                                    : URL.createObjectURL(file)
                                )
                              }
                              className="text-primary underline cursor-pointer"
                            >
                              {file.name || file.fileName}{" "}
                            </div>
                            {!isAdd ? (
                              <></>
                            ) : (
                              <Trash2
                                className="stroke-red-600 h-4 cursor-pointer"
                                onClick={onDeleteAttachment(index)}
                              />
                            )}
                          </div>
                        ))
                    ) : (
                      <></>
                    )}
                  </div>
                  <FormTextarea
                    name={DESCRIPTION}
                    control={form.control}
                    label="Description"
                    placeholder="Please enter"
                    error={errors[DESCRIPTION]?.message}
                    isRequired={requiredFields.includes(DESCRIPTION)}
                    disabled={!isAdd}
                  />
                  <FormTextarea
                    name={COMMENTS}
                    control={form.control}
                    label="Comments"
                    placeholder="Please enter"
                    error={errors[COMMENTS]?.message}
                    isRequired={requiredFields.includes(COMMENTS)}
                    disabled={isView}
                  />
                </div>
              </div>
              <div className="flex flex-row justify-end gap-3  py-3">
                <Button
                  variant="outline"
                  type="button"
                  onClick={onCancel}
                  disabled={isAddPending || isUpdatePending}
                >
                  Cancel
                </Button>
                {isView ? (
                  <></>
                ) : (
                  <Button
                    type="submit"
                    disabled={isAddPending || isUpdatePending}
                  >
                    {isAddPending ||
                      (isUpdatePending && <Loader variant="secondary" />)}
                    Save
                  </Button>
                )}
              </div>
            </form>
          </FormProvider>
          <div className="bg-white mt-[20px] rounded-lg px-[30px] py-[25px]">
            <FormSectionHeader>Comment History</FormSectionHeader>
            <div className="mt-3 max-h-[360px] overflow-y-auto">
              {incidentComments?.data?.length ? (
                incidentComments?.data?.map((log: any, index: number) => {
                  return (
                    <p key={`audit-${index + 1}`} className="text-[12px]">
                      On{" "}
                      <b className="font-[600]">
                        {log?.createdAt
                          ? moment(log?.createdAt).format("MM/DD/YYYY")
                          : ""}
                      </b>{" "}
                      {log?.firstName} {log?.lastName} - {log?.comments}
                    </p>
                  );
                })
              ) : (
                <p className="text-[14px]">No History</p>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AddEdit;
